import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { SupportedBy } from "../components/Sponsors";
import { Trophy, ArrowRight, ArrowLeft, X, Sparkles, Linkedin, ChevronDown, Users, Medal, Award } from "lucide-react";
import { formatTag } from "../lib/format";

const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const RANK_ICON = { 1: Trophy, 2: Medal, 3: Award };

export default function ResultsEdition() {
  const { slug } = useParams();
  const [data, setData] = useState(undefined); // undefined=loading, null=404
  const [project, setProject] = useState(null);

  useEffect(() => {
    api.get(`/results/edition/${slug}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [slug]);

  const viewProject = useCallback(async (teamId) => {
    setProject({ loading: true });
    try {
      const { data } = await api.get(`/results/edition/${slug}/project/${teamId}`);
      setProject(data);
    } catch { setProject({ error: true }); }
  }, [slug]);

  if (data === undefined) return <div className="App"><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><span className="spinner" /></div><Footer /></div>;
  if (data === null) return (
    <div className="App" data-testid="results-edition-404">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 py-24 text-center min-h-[50vh]">
        <h1 className="font-display text-4xl text-white">Results not available</h1>
        <p className="text-gray-400 mt-4">These results haven't been announced yet, or the link is incorrect.</p>
        <Link to="/results" className="btn-secondary inline-flex items-center gap-2 mt-6"><ArrowLeft className="w-4 h-4" /> Back to archive</Link>
      </div>
      <Footer />
    </div>
  );

  const winners = data.winners || [];
  const r1 = winners.find(w => w.rank === 1);
  const r2 = winners.find(w => w.rank === 2);
  const r3 = winners.find(w => w.rank === 3);

  return (
    <div className="App" data-testid="results-edition-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        {/* Hero */}
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <Link to="/results" className="hover:text-brand">Results</Link> <span>→</span> <span className="text-gray-300">{data.edition_label}</span>
        </div>
        <h1 className="font-display text-4xl sm:text-6xl text-white mt-4">{data.edition_label} — Results</h1>
        <p className="text-gray-400 mt-4 text-lg max-w-2xl">Thank you to every team that competed. Here are our winners.</p>
        {data.announced_at && <p className="text-xs text-gray-500 mt-2">Announced {new Date(data.announced_at).toLocaleString()}</p>}

        {/* Podium */}
        <div className="mt-14 grid md:grid-cols-3 gap-5 items-end" data-testid="winners-podium">
          <PodiumCard w={r2} onView={viewProject} />
          <PodiumCard w={r1} onView={viewProject} elevated />
          <PodiumCard w={r3} onView={viewProject} />
        </div>

        {/* Finalists */}
        {(data.finalists || []).length > 0 && (
          <div className="mt-20" data-testid="finalists-section">
            <h2 className="font-display text-3xl text-white">Round 2 Finalists</h2>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.finalists.map((f) => (
                <div key={f.team_id} className="card-surface" data-testid={`finalist-${f.team_id}`}>
                  <div className="text-white font-medium">{f.team_name}</div>
                  <div className="text-sm text-gray-400 mt-1">{f.project_title}</div>
                  <div className="text-xs text-gray-500 mt-2">{f.college}</div>
                  <MeetTheTeam members={f.members} idKey={f.team_id} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats bar hidden for now (low team count) — restore this block when numbers are healthy */}

        {/* CTA */}
        <div className="mt-16 flex flex-wrap gap-4">
          <Link to="/editions" className="btn-primary flex items-center gap-2" data-testid="cta-next-edition"><Sparkles className="w-4 h-4" /> Explore the next edition</Link>
        </div>
      </div>

      {project && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-5" onClick={() => setProject(null)} data-testid="project-modal">
          <div className="card-surface max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start">
              <div className="label-eyebrow">Project Summary</div>
              <button onClick={() => setProject(null)} className="text-gray-400 hover:text-white" data-testid="close-project"><X className="w-5 h-5" /></button>
            </div>
            {project.loading ? <div className="py-8 text-center"><span className="spinner" /></div> : project.error ? (
              <div className="py-8 text-center" data-testid="project-unavailable">
                <h3 className="font-display text-xl text-white">Project details unavailable</h3>
                <p className="text-sm text-gray-400 mt-2">This team's submission summary couldn't be loaded for this edition.</p>
              </div>
            ) : (
              <>
                <h3 className="font-display text-2xl text-white mt-2">{project.project_title || "—"}</h3>
                <div className="text-sm text-brand">{project.team_name} · {project.college}</div>
                {project.public_consent === false ? (
                  <div className="mt-6 p-4 border border-white/10 bg-white/5 text-center" data-testid="project-private">
                    <p className="text-sm text-gray-400">This team has kept their project details private.</p>
                  </div>
                ) : (
                <div className="mt-5 space-y-4">
                  {project.problem_statement && <PField label="Problem">{project.problem_statement}</PField>}
                  {project.proposed_solution && <PField label="Proposed Solution">{project.proposed_solution}</PField>}
                  {project.target_audience && <PField label="Target Audience">{project.target_audience}</PField>}
                  {project.market_size && <PField label="Market Size">{project.market_size}</PField>}
                  {project.build_next && <PField label="What they'd build next">{project.build_next}</PField>}
                  <div className="flex flex-wrap gap-2">
                    {project.category && <span className="badge badge-invited">{formatTag(project.category)}</span>}
                    {project.innovation_type && <span className="badge badge-invited">{formatTag(project.innovation_type)}</span>}
                    {project.idea_stage && <span className="badge badge-invited">{formatTag(project.idea_stage)}</span>}
                  </div>
                  {(project.tech_stack || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {project.tech_stack.map(t => <span key={t} className="px-2 py-1 text-[11px] font-mono uppercase tracking-widest bg-white/5 border border-white/10 text-gray-300">{t}</span>)}
                    </div>
                  )}
                </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      <SupportedBy sponsors={data.sponsors} />
      <Footer />
    </div>
  );
}

const PodiumCard = ({ w, onView, elevated }) => {
  if (!w) return <div className="hidden md:block" />;
  const Ic = RANK_ICON[w.rank] || Trophy;
  return (
    <div className={`card-surface text-center ${elevated ? "border-2 border-[#FBBF24]/50 md:-mt-8 md:pb-10" : "border border-white/10"}`} data-testid={`winner-rank-${w.rank}`}>
      <Ic className={`mx-auto text-[#FBBF24] ${elevated ? "w-14 h-14" : "w-11 h-11"}`} strokeWidth={1.5} />
      <div className="label-eyebrow mt-3 text-[#FBBF24]">{w.rank_label}</div>
      <div className={`font-display text-white mt-2 ${elevated ? "text-3xl" : "text-2xl"}`}>{w.team_name}</div>
      <div className="text-sm text-gray-400 mt-1">{w.project_title}</div>
      <MeetTheTeam members={w.members} idKey={w.team_id} center />
      <div className="text-xs text-gray-500 mt-1">{w.college}</div>
      <div className="text-[#FBBF24] font-bold mt-3">{formatINR(w.prize_amount)}</div>
      <button onClick={() => onView(w.team_id)} className="text-brand text-sm mt-4 inline-flex items-center gap-1 hover:gap-2 transition-[gap]" data-testid={`view-project-${w.team_id}`}>
        View Project <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};

const MeetTheTeam = ({ members, idKey, center }) => {
  const [open, setOpen] = useState(false);
  if (!members || members.length === 0) return null;
  return (
    <div className={`mt-3 ${center ? "flex flex-col items-center" : ""}`} data-testid={`meet-team-${idKey}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-xs label-eyebrow text-brand hover:text-white transition-colors"
        data-testid={`meet-team-toggle-${idKey}`}
      >
        <Users className="w-3.5 h-3.5" strokeWidth={1.5} />
        Meet the Team
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className={`mt-2 space-y-1.5 ${center ? "inline-block text-left" : ""}`} data-testid={`meet-team-list-${idKey}`}>
          {members.map((m, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
              <span>{m.name}{m.is_leader && <span className="text-gray-500 text-xs"> · Lead</span>}</span>
              {m.linkedin && (
                <a href={m.linkedin} target="_blank" rel="noopener noreferrer"
                   className="text-[#5BA4F0] hover:text-white transition-colors"
                   title={`${m.name} on LinkedIn`} data-testid={`member-linkedin-${idKey}-${i}`}>
                  <Linkedin className="w-3.5 h-3.5" strokeWidth={1.5} />
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PField = ({ label, children }) => (
  <div>
    <div className="label-eyebrow mb-1">{label}</div>
    <div className="text-sm text-gray-300 leading-relaxed">{children}</div>
  </div>
);
